/**
 * Standardized error handling utilities.
 *
 * Provides consistent error handling patterns across the application.
 * Standardizes how errors are logged, thrown, and returned.
 *
 * @module errorHandling
 */

import { logger } from './logger';
import { handleApiError, type ApiError } from './apiErrorHandler';

/**
 * Handles API errors with standardized pattern.
 *
 * Wrapper around handleApiError that provides consistent error handling
 * for API operations. Logs error and shows user notification.
 * Execution continues after handling (errors are logged but not thrown).
 *
 * @param error - The error to handle
 * @param defaultMessage - Default error message
 * @param context - Context for logging
 * @returns ApiError object
 *
 * @example
 * ```typescript
 * try {
 *   await apiCall();
 * } catch (error) {
 *   handleApiErrorStandardized(error, 'Failed to save', 'saveData');
 *   // Error is logged and toast shown, execution continues
 * }
 * ```
 */
export function handleApiErrorStandardized(
  error: unknown,
  defaultMessage: string,
  context?: string,
): ApiError | null {
  const apiError = handleApiError(error, defaultMessage, context);
  return apiError;
}

/**
 * Validates a required value and throws standardized error if missing.
 *
 * @param value - Value to validate
 * @param errorMessage - Error message if value is missing
 * @param context - Context for logging
 * @returns The value if valid
 * @throws Error if value is null/undefined
 *
 * @example
 * ```typescript
 * const userId = requireValue(user?.id, 'User ID is required', 'saveData');
 * ```
 */
export function requireValue<T>(
  value: T | null | undefined,
  errorMessage: string,
  context?: string,
): T {
  if (value === null || value === undefined) {
    const error = new Error(errorMessage);
    logger.error(`Validation error [${context || 'unknown'}]`, error);
    throw error;
  }
  return value;
}

/**
 * Handles non-critical warnings consistently.
 *
 * Logs a warning without throwing or returning error.
 * Use for situations that are unexpected but don't prevent execution.
 *
 * @param message - Warning message
 * @param context - Context for logging
 * @param data - Additional data for logging
 *
 * @example
 * ```typescript
 * if (!result) {
 *   logWarning('Failed to save optional data', 'saveOptionalData', { id });
 *   return; // Continue execution
 * }
 * ```
 */
export function logWarning(
  message: string,
  context?: string,
  data?: Record<string, unknown>,
): void {
  const logContext = context ? `[${context}]` : '';
  logger.warn(`Warning ${logContext}: ${message}`, data);
}

